import React, { useEffect, useState, useCallback } from "react";
import api from "../../lib/api";
import { useToast } from "../../hooks/use-toast";
import SeoFields, { inputCls, labelCls, emptySeo, slugify } from "./SeoFields";
import { Plus, Pencil, Trash2, Loader2, X, Save, ExternalLink } from "lucide-react";

const blank = { title: "", slug: "", content: "", status: "published", seo: { ...emptySeo } };

const PagesManager = () => {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // object or null
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/pages");
      setItems(data);
    } catch (e) { toast({ title: "Failed to load pages", variant: "destructive" }); }
    finally { setLoading(false); }
  }, [toast]);
  useEffect(() => { load(); }, [load]);

  const startNew = () => setEditing({ ...blank, seo: { ...emptySeo } });
  const startEdit = (p) => setEditing(JSON.parse(JSON.stringify(p)));

  const save = async () => {
    if (!editing.title || !editing.slug) { toast({ title: "Title and slug required", variant: "destructive" }); return; }
    setSaving(true);
    try {
      if (editing.id) {
        await api.put(`/admin/pages/${editing.id}`, editing);
        toast({ title: "Page updated" });
      } else {
        await api.post("/admin/pages", editing);
        toast({ title: "Page created" });
      }
      setEditing(null);
      load();
    } catch (e) {
      toast({ title: e.response?.data?.detail || "Save failed", variant: "destructive" });
    } finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    try { await api.delete(`/admin/pages/${p.id}`); toast({ title: "Deleted" }); load(); }
    catch (e) { toast({ title: "Delete failed", variant: "destructive" }); }
  };

  if (editing) {
    return (
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl font-bold text-white">{editing.id ? "Edit Page" : "New Page"}</h2>
          <button onClick={() => setEditing(null)} className="text-gray-400 hover:text-white"><X size={22} /></button>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Title</label>
            <input className={inputCls} value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value, slug: editing.id ? editing.slug : slugify(e.target.value) })} />
          </div>
          <div>
            <label className={labelCls}>Slug (URL)</label>
            <input className={inputCls} value={editing.slug} readOnly={editing.core} onChange={(e) => setEditing({ ...editing, slug: slugify(e.target.value) })} />
            {editing.core && <p className="text-[11px] text-gray-500 mt-1">Built-in page — slug locked. You can edit content & SEO.</p>}
          </div>
        </div>
        <div>
          <label className={labelCls}>Content (HTML supported)</label>
          <textarea rows={8} className={inputCls} value={editing.content} onChange={(e) => setEditing({ ...editing, content: e.target.value })} placeholder="<p>Your page content...</p>" />
        </div>
        <div>
          <label className={labelCls}>Status</label>
          <select className={inputCls} value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })}>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
          </select>
        </div>
        <SeoFields seo={editing.seo} onChange={(seo) => setEditing({ ...editing, seo })} />
        <div className="flex gap-3">
          <button onClick={save} disabled={saving} className="btn-neon inline-flex items-center gap-2 px-6 py-2.5 rounded-lg font-semibold">
            {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />} Save Page
          </button>
          <button onClick={() => setEditing(null)} className="px-6 py-2.5 rounded-lg border border-white/15 text-gray-300 hover:bg-white/5">Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-xl font-bold text-white">Pages</h2>
        <button onClick={startNew} className="btn-neon inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-sm"><Plus size={18} /> New Page</button>
      </div>
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#00ff87]" size={32} /></div>
      ) : items.length === 0 ? (
        <p className="text-gray-400 py-10 text-center">No pages yet. Create your first page.</p>
      ) : (
        <div className="space-y-3">
          {items.map((p) => (
            <div key={p.id} className="flex items-center justify-between gap-4 rounded-xl border border-white/10 bg-[#0e0f1a] p-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-white truncate">{p.title}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${p.status === "published" ? "bg-[#00ff87]/15 text-[#00ff87]" : "bg-yellow-500/15 text-yellow-500"}`}>{p.status}</span>
                </div>
                <span className="text-xs text-gray-500">/{p.slug}</span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <a href={p.path || `/${p.slug}`} target="_blank" rel="noreferrer" className="h-9 w-9 grid place-items-center rounded-lg bg-white/5 text-gray-400 hover:text-[#00e5ff]"><ExternalLink size={16} /></a>
                <button onClick={() => startEdit(p)} className="h-9 w-9 grid place-items-center rounded-lg bg-white/5 text-gray-400 hover:text-[#00ff87]"><Pencil size={16} /></button>
                {p.core ? (
                  <span className="h-9 px-2 grid place-items-center rounded-lg bg-white/5 text-[10px] text-gray-500" title="Built-in page">CORE</span>
                ) : (
                  <button onClick={() => remove(p)} className="h-9 w-9 grid place-items-center rounded-lg bg-white/5 text-gray-400 hover:text-red-500"><Trash2 size={16} /></button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PagesManager;
